import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { BarChart3, Eye, Users, Clock, TrendingUp, RefreshCw } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell
} from 'recharts';
import { useAdmin } from './AdminContext';

export default function TrafficTab({ refreshTrigger }) {
  const { adminFetch, API_URL } = useAdmin();
  const [trafficStats, setTrafficStats] = useState(null);
  const [trafficPeriod, setTrafficPeriod] = useState(30);
  const [realtimeTraffic, setRealtimeTraffic] = useState(null);

  const loadTrafficStats = useCallback(async (period) => {
    try {
      const days = period ?? trafficPeriod;
      const response = await adminFetch(`${API_URL}/api/v2/analytics/summary?days=${days}`);
      if (response.ok) {
        const data = await response.json();
        setTrafficStats(data);
      }
    } catch (error) {
      console.error('Error loading traffic stats:', error);
    }
  }, [adminFetch, API_URL, trafficPeriod]);

  const loadRealtimeTraffic = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/analytics/realtime`);
      if (response.ok) {
        const data = await response.json();
        setRealtimeTraffic(data);
      }
    } catch (error) {
      console.error('Error loading realtime traffic:', error);
    }
  }, [adminFetch, API_URL]);

  useEffect(() => {
    loadTrafficStats();
    loadRealtimeTraffic();
  }, [refreshTrigger]);

  return (
    <div className="space-y-6" data-testid="traffic-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-cyan-400" />
          Website Traffic Analytics
        </h2>
        <div className="flex items-center gap-4">
          {realtimeTraffic && (
            <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-green-400 text-sm font-medium">
                {realtimeTraffic.active_users} active now
              </span>
            </div>
          )}
          <select
            value={trafficPeriod}
            onChange={(e) => {
              const newPeriod = Number(e.target.value);
              setTrafficPeriod(newPeriod);
              loadTrafficStats(newPeriod);
            }}
            className="bg-slate-700 border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadTrafficStats(); loadRealtimeTraffic(); }}
            className="border-slate-600"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {trafficStats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Total Page Views</p>
                    <p className="text-3xl font-bold text-white">{trafficStats.total_page_views.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-1">Last {trafficPeriod} days</p>
                  </div>
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center">
                    <Eye className="h-6 w-6 text-cyan-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Unique Visitors</p>
                    <p className="text-3xl font-bold text-white">{trafficStats.unique_visitors_month.toLocaleString()}</p>
                    <p className="text-xs text-green-400 mt-1">
                      Today: {trafficStats.unique_visitors_today} | Week: {trafficStats.unique_visitors_week}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Avg Session Duration</p>
                    <p className="text-3xl font-bold text-white">
                      {trafficStats.avg_session_duration > 60
                        ? `${Math.round(trafficStats.avg_session_duration / 60)}m ${Math.round(trafficStats.avg_session_duration % 60)}s`
                        : `${trafficStats.avg_session_duration}s`
                      }
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Average time on site</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                    <Clock className="h-6 w-6 text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Top Page</p>
                    <p className="text-2xl font-bold text-white capitalize">
                      {Object.keys(trafficStats.page_views_by_page)[0] || 'N/A'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {Object.values(trafficStats.page_views_by_page)[0]?.toLocaleString() || 0} views
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-amber-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Daily Traffic</CardTitle>
                <CardDescription>Page views and unique visitors over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trafficStats.daily_views}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fontSize: 12 }} tickFormatter={(value) => value.slice(5)} />
                    <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} labelStyle={{ color: '#F3F4F6' }} />
                    <Line type="monotone" dataKey="views" stroke="#06B6D4" strokeWidth={2} name="Page Views" dot={false} />
                    <Line type="monotone" dataKey="unique" stroke="#8B5CF6" strokeWidth={2} name="Unique Visitors" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Views by Page</CardTitle>
                <CardDescription>Traffic distribution across pages</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={Object.entries(trafficStats.page_views_by_page).map(([page, views]) => ({ page, views }))}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis type="number" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="page" stroke="#9CA3AF" tick={{ fontSize: 12 }} width={80} />
                    <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} labelStyle={{ color: '#F3F4F6' }} />
                    <Bar dataKey="views" fill="#06B6D4" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Device Breakdown */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Device Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <RechartsPie>
                    <Pie
                      data={Object.entries(trafficStats.device_breakdown).map(([name, value]) => ({ name, value }))}
                      cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={5} dataKey="value"
                    >
                      {Object.keys(trafficStats.device_breakdown).map((_, index) => (
                        <Cell key={`device-${index}`} fill={['#06B6D4', '#8B5CF6', '#F59E0B'][index % 3]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 justify-center mt-2">
                  {Object.entries(trafficStats.device_breakdown).map(([device, count], i) => (
                    <span key={device} className="text-xs text-slate-400">
                      <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: ['#06B6D4', '#8B5CF6', '#F59E0B'][i % 3] }} />
                      {device}: {count}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Browser Breakdown */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Browser Types</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <RechartsPie>
                    <Pie
                      data={Object.entries(trafficStats.browser_breakdown).map(([name, value]) => ({ name, value }))}
                      cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={5} dataKey="value"
                    >
                      {Object.keys(trafficStats.browser_breakdown).map((_, index) => (
                        <Cell key={`browser-${index}`} fill={['#10B981', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6'][index % 5]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 justify-center mt-2">
                  {Object.entries(trafficStats.browser_breakdown).slice(0, 5).map(([browser, count], i) => (
                    <span key={browser} className="text-xs text-slate-400">
                      <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: ['#10B981', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6'][i % 5] }} />
                      {browser}: {count}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Referrer Sources */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Traffic Sources</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <RechartsPie>
                    <Pie
                      data={Object.entries(trafficStats.referrer_breakdown).map(([name, value]) => ({ name, value }))}
                      cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={5} dataKey="value"
                    >
                      {Object.keys(trafficStats.referrer_breakdown).map((_, index) => (
                        <Cell key={`ref-${index}`} fill={['#22C55E', '#3B82F6', '#EC4899', '#F97316', '#6366F1', '#14B8A6'][index % 6]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 justify-center mt-2">
                  {Object.entries(trafficStats.referrer_breakdown).slice(0, 4).map(([source, count], i) => (
                    <span key={source} className="text-xs text-slate-400">
                      <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: ['#22C55E', '#3B82F6', '#EC4899', '#F97316'][i % 4] }} />
                      {source}: {count}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Geographic */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Geographic</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(trafficStats.geographic_breakdown).slice(0, 6).map(([country, count]) => (
                    <div key={country} className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">{country}</span>
                      <span className="text-sm text-slate-400">{count}</span>
                    </div>
                  ))}
                  {Object.keys(trafficStats.geographic_breakdown).length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Geographic data requires geo-IP service integration
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {!trafficStats && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-12 text-center">
            <BarChart3 className="h-12 w-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No traffic data available yet.</p>
            <p className="text-slate-500 text-sm mt-2">Traffic will be tracked as users visit your site.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
